/*!
 * Ashland Leather site-wide engagement tracker  v1.0.4  (2026-10-26)
 * Single file, no dependencies, ES5, passive listeners, safe if gtag/dataLayer missing.
 * Sends GA4 events through the same path the live "Find your wallet" chooser already uses:
 *   window.gtag('event', ...) if it exists, else dataLayer.push(arguments) (GTM-KX6G9WZX Google tag, G-C8LFSSQYQY).
 * Kill switches: localStorage al_eng_off=1, or remove the one render line in layout/theme.liquid.
 * Debug: add ?al_eng_debug=1 to any URL (console log, debug_mode param, window.__alEngLog ring buffer).
 * Reads config from window.__alEngCfg (set by snippets/al-engagement.liquid); falls back to ShopifyAnalytics.meta.
 * Never reads form field values. Never sends query strings, emails, or order data.
 * Changelog v1.0.2: cart_qty_change now fires once per real change (counted on the theme's change event, labelled plus/minus; a +/- click that changes nothing sends nothing);
 *   ?al_eng_debug=1 sends debug_mode as boolean true and sets it on the Google tag (DebugView); article/blog pages emit eng_section_view
 *   (headings as parts, tall sections use a viewport-based threshold); section id suffix strip no longer eats hpr_find_wallet; strips of 40px+ are observed.
 * Changelog v1.0.3: clicks with event.isTrusted === false (scripts calling .click(), for example the Zipify OneClickUpsell app re-clicking checkout
 *   about 0.8 s after a real click) are ignored by every click-based event; real clicks and Enter or Space on a button are trusted and always counted;
 *   checkout click throttle raised from 800 ms to 2000 ms as a second guard.
 * Changelog v1.0.4: fixes phones. On a touch tap the Zipify OneClickUpsell app cancels touchend on add to cart and checkout and then clicks by script, so
 *   v1.0.3 (which dropped every untrusted click) counted nothing on phones. Now an untrusted click is dropped when a trusted click on the same button happened in
 *   the last 3 s (desktop and keyboard: Zipify's re-click 0.7 to 1 s later), and counted ONCE when it follows a trusted pointerup/touchend on that button within 3 s
 *   with no trusted click (the phone tap). Untrusted clicks with no real input before them are still ignored. Add to cart, buy now and checkout throttle is 2000 ms.
 * Event families: eng_* (all pages), pdp_* (product), col_* (collection/search/product links), cart_* (cart page and drawer), art_* (article).
 */
(function () {
  'use strict';
  var W = window, D = document;
  if (W.__alEngLoaded) { return; }
  try { if (W.localStorage && W.localStorage.getItem('al_eng_off') === '1') { return; } } catch (e0) {}
  W.__alEngLoaded = true;

  var VERSION = '1.0.4';
  var DEBUG = /[?&]al_eng_debug=1/.test(W.location.search);
  var cfg = W.__alEngCfg || {};

  /* ---------- page identity ---------- */
  var rawType = cfg.shopify_page_type || '';
  try {
    if (!rawType && W.ShopifyAnalytics && W.ShopifyAnalytics.meta && W.ShopifyAnalytics.meta.page) {
      rawType = W.ShopifyAnalytics.meta.page.pageType || '';
    }
  } catch (e1) {}
  var TYPE_MAP = { index: 'homepage', product: 'product', collection: 'collection', 'list-collections': 'collection_list',
    cart: 'cart', blog: 'blog', article: 'article', page: 'page', search: 'search', '404': '404' };
  var PT = TYPE_MAP[rawType] || (rawType && /^(customers|password|gift_card|policy)/.test(rawType) ? '' : 'other');
  if (!PT) { return; } /* account, password, gift card pages: not instrumented */
  var path = W.location.pathname || '/';
  var handle = cfg.handle || '';
  if (!handle) {
    var mm = path.match(/\/(?:products|collections|pages|blogs\/[^\/]+)\/([^\/?#]+)/);
    handle = mm ? mm[1] : (PT === 'homepage' ? 'home' : '');
  }
  var isProduct = PT === 'product';

  /* ---------- helpers ---------- */
  function clean(s, n) { return String(s == null ? '' : s).replace(/\s+/g, ' ').trim().slice(0, n || 80); }
  function closest(el, sel) { try { return el && el.closest ? el.closest(sel) : null; } catch (e) { return null; } }
  function pathOf(u) {
    try {
      if (!u) { return ''; }
      if (/^(mailto|tel):/i.test(u)) { return u.split(':')[0] + ':'; }
      var a = D.createElement('a'); a.href = u;
      var h = a.hostname === W.location.hostname ? '' : a.hostname;
      return clean(h + a.pathname + (a.hash && a.hash.length < 40 ? a.hash : ''), 100);
    } catch (e) { return ''; }
  }
  function now() { return Date.now(); }
  var lastAct = now();
  var sent = 0, MAX_EVENTS = 250;
  var seen = {};               /* once-per-page-view keys */
  var recent = {};             /* throttle keys */
  function once(k) { if (seen[k]) { return false; } seen[k] = 1; return true; }
  function throttled(k, ms) { var t = now(); if (recent[k] && t - recent[k] < ms) { return true; } recent[k] = t; return false; }
  var leatherRe = /(shell cordovan|dublin|chromexcel|chrome ?xl|cxl|illini|latigo|derby|essex|bison|color ?#?\s?8|western)/i;

  function scrollPct() {
    try {
      var de = D.documentElement, b = D.body;
      var total = Math.max(de.scrollHeight, b ? b.scrollHeight : 0);
      if (!total) { return 0; }
      var y = W.pageYOffset || de.scrollTop || 0;
      return Math.min(100, Math.round(((y + (W.innerHeight || de.clientHeight || 0)) / total) * 100));
    } catch (e) { return 0; }
  }
  function sectionName(el) {
    try {
      var ts = closest(el, '[data-track-section]');
      if (ts) { return clean(ts.getAttribute('data-track-section'), 40); }
      if (closest(el, 'header, .shopify-section-group-header-group, #shopify-section-header')) { return 'header'; }
      if (closest(el, 'footer, .shopify-section-group-footer-group')) { return 'footer'; }
      if (closest(el, 'cart-drawer, #CartDrawer, .cart-drawer')) { return 'cart_drawer'; }
      var s = closest(el, '.shopify-section[id]');
      if (!s) { return 'page'; }
      return clean(s.id.replace(/^shopify-section-/, '').replace(/^template--\d+__/, '').replace(/_(?=[A-Za-z0-9]*[A-Z0-9])[A-Za-z0-9]{6}$/, ''), 40);
    } catch (e) { return 'page'; }
  }
  function selectedOptions() {
    var out = [], i, els;
    try {
      els = D.querySelectorAll('variant-selects input[type="radio"]:checked, variant-selects select');
      for (i = 0; i < els.length; i++) { out.push(clean(els[i].value, 40)); }
    } catch (e) {}
    return out.join(' / ');
  }
  function productCtx() {
    var o = {};
    if (!isProduct) { return o; }
    if (cfg.product_type) { o.product_type = clean(cfg.product_type, 40); }
    if (cfg.product_price) { o.product_price = cfg.product_price; }
    var v = selectedOptions();
    if (v) { o.product_variant = v; }
    var cur = null;
    try { cur = D.querySelector('.custom__bundle__section .current_pd .title_cmb'); } catch (e) {}
    var color = cur ? clean(cur.textContent, 40) : '';
    if (color) { o.product_color = color; }
    var m = leatherRe.exec((cfg.title || '') + ' ' + (v || '') + ' ' + color + ' ' + handle);
    if (m) { o.product_leather = clean(m[1], 30).toLowerCase(); }
    return o;
  }

  /* ---------- sender ---------- */
  function send(name, p, withProduct) {
    try {
      if (sent >= MAX_EVENTS) { return; }
      var o = { page_type: PT, page_handle: handle, eng_v: VERSION }, k;
      if (cfg.template) { o.page_template = cfg.template; }
      if (PT === 'search' && cfg.search_term) { o.search_term = clean(cfg.search_term, 60); }
      if (withProduct && isProduct) { var pc = productCtx(); for (k in pc) { if (pc.hasOwnProperty(k)) { o[k] = pc[k]; } } }
      for (k in p) { if (p.hasOwnProperty(k) && p[k] !== undefined && p[k] !== null && p[k] !== '') { o[k] = typeof p[k] === 'string' ? p[k].slice(0, 100) : p[k]; } }
      if (DEBUG) { o.debug_mode = true; }
      sent++;
      try { (W.__alEngLog = W.__alEngLog || []).push({ t: now(), n: name, p: o }); if (W.__alEngLog.length > 100) { W.__alEngLog.shift(); } } catch (e2) {}
      if (DEBUG && W.console) { W.console.log('[al-eng]', name, o); }
      if (typeof W.gtag === 'function') { W.gtag('event', name, o); }
      else {
        W.dataLayer = W.dataLayer || [];
        (function () { W.dataLayer.push(arguments); })('event', name, o);
      }
    } catch (e) {}
  }

  /* ---------- activity ---------- */
  function touch() { lastAct = now(); }
  var ACT = ['mousemove', 'keydown', 'touchstart', 'scroll', 'click'], ai;
  for (ai = 0; ai < ACT.length; ai++) { try { D.addEventListener(ACT[ai], touch, { passive: true, capture: true }); } catch (e3) { D.addEventListener(ACT[ai], touch, true); } }

  /* ---------- scroll depth + article read ---------- */
  var MARKS = [25, 50, 75, 90], curScroll = 0, scrollQueued = false;
  function checkScroll() {
    scrollQueued = false;
    curScroll = scrollPct();
    var i;
    for (i = 0; i < MARKS.length; i++) {
      if (curScroll >= MARKS[i] && once('s' + MARKS[i])) { send('eng_scroll', { percent_scrolled: MARKS[i] }, true); }
    }
    checkRead();
  }
  function onScroll() { if (!scrollQueued) { scrollQueued = true; setTimeout(checkScroll, 250); } }
  try { W.addEventListener('scroll', onScroll, { passive: true }); } catch (e4) { W.addEventListener('scroll', onScroll); }
  W.addEventListener('resize', onScroll);

  /* ---------- engaged time ---------- */
  var engaged = 0, TIMES = [15, 30, 60, 120, 180, 300];
  function checkRead() {
    if (PT === 'article' && curScroll >= 75 && engaged >= 30 && once('art_read')) {
      send('art_read', { percent_scrolled: curScroll, engaged_seconds: engaged });
    }
  }
  var timer = W.setInterval(function () {
    try {
      if (D.visibilityState !== 'visible') { return; }
      if (now() - lastAct > 30000) { return; }
      engaged++;
      var i;
      for (i = 0; i < TIMES.length; i++) { if (engaged === TIMES[i]) { send('eng_time', { engaged_seconds: engaged }, true); } }
      if (engaged === 300) { W.clearInterval(timer); }
      if (engaged % 5 === 0) { checkRead(); }
    } catch (e) {}
  }, 1000);

  /* ---------- section views ---------- */
  function observeSections() {
    try {
      if (!('IntersectionObserver' in W)) { return; }
      var list = D.querySelectorAll('#MainContent .shopify-section[id]'), n = 0, i, vh = W.innerHeight || 800;
      var obsByTh = {};
      function report(entries, io) {
        var j, tg, nm, extra;
        for (j = 0; j < entries.length; j++) {
          if (entries[j].isIntersecting) {
            tg = entries[j].target;
            nm = tg.getAttribute('data-al-nm') || sectionName(tg);
            extra = tg.getAttribute('data-al-lt');
            if (once('sv' + nm)) { send('eng_section_view', { section: nm, item_index: Number(tg.getAttribute('data-al-i')) || undefined, link_text: extra || undefined }); }
            io.unobserve(tg);
          }
        }
      }
      function obsFor(th) {
        var key = String(th);
        if (!obsByTh[key]) { obsByTh[key] = new W.IntersectionObserver(report, { threshold: th }); }
        return obsByTh[key];
      }
      for (i = 0; i < list.length && n < 30; i++) {
        if (list[i].offsetHeight < 40) { continue; }
        list[i].setAttribute('data-al-i', String(++n)); /* attribute only, no visual effect */
        /* tall sections (blog/article body is one 9000px section): 30% can never be on screen, so use a viewport based threshold */
        var h = list[i].offsetHeight, th = 0.3;
        if ((PT === 'article' || PT === 'blog') && h > vh * 2) { th = Math.max(0.02, Math.min(0.3, (vh * 0.5) / h)); }
        obsFor(th).observe(list[i]);
      }
      /* article pages: each h2/h3 in the body counts as a part of the article */
      if (PT === 'article') {
        var hs = D.querySelectorAll('.article-template__content h2, .article-template__content h3'), k, part = 0;
        if (!hs.length) { hs = D.querySelectorAll('#MainContent article h2, #MainContent article h3'); }
        for (k = 0; k < hs.length && n < 30; k++) {
          if (!clean(hs[k].textContent, 60)) { continue; }
          part++; n++;
          hs[k].setAttribute('data-al-nm', 'article_part_' + part);
          hs[k].setAttribute('data-al-lt', clean(hs[k].textContent, 60));
          hs[k].setAttribute('data-al-i', String(part));
          obsFor(0.5).observe(hs[k]);
        }
      }
    } catch (e) {}
  }

  /* ---------- video ---------- */
  var vids = [];
  function vinfo(v) {
    var idx = vids.indexOf(v); if (idx < 0) { vids.push(v); idx = vids.length - 1; }
    var src = '';
    try { src = pathOf(v.currentSrc || (v.querySelector('source') || {}).src || ''); } catch (e) {}
    return { v: v, idx: idx + 1, title: clean(v.getAttribute('aria-label') || v.getAttribute('title') || '', 80), url: src, state: v.__al || (v.__al = { first: false, plays: 0, p: {}, done: false }) };
  }
  function skipVideo(v) {
    if (closest(v, '.hpr')) { return true; }                       /* hero video already tracked by hero_video_* */
    if (v.loop && v.muted && !v.controls && !closest(v, 'deferred-media')) { return true; } /* decorative loop */
    return false;
  }
  function onPlay(e) {
    try {
      var v = e.target; if (!v || v.tagName !== 'VIDEO' || skipVideo(v)) { return; }
      var i = vinfo(v), st = i.state;
      st.plays++;
      if (!st.first) { st.first = true; send('eng_video', { video_action: 'first_play', video_title: i.title, video_url: i.url, item_index: i.idx, section: sectionName(v) }, true); }
      else if (st.plays <= 5 && !throttled('vp' + i.idx, 1500)) { send('eng_video', { video_action: 'play', video_title: i.title, video_url: i.url, item_index: i.idx, section: sectionName(v) }, true); }
    } catch (e1) {}
  }
  function onTime(e) {
    try {
      var v = e.target; if (!v || v.tagName !== 'VIDEO' || !v.duration || skipVideo(v)) { return; }
      var pct = (v.currentTime / v.duration) * 100, marks = [25, 50, 75], k, i = vinfo(v);
      for (k = 0; k < marks.length; k++) {
        if (pct >= marks[k] && !i.state.p[marks[k]]) { i.state.p[marks[k]] = 1; send('eng_video', { video_action: 'progress_' + marks[k], video_title: i.title, video_url: i.url, item_index: i.idx, section: sectionName(v) }, true); }
      }
    } catch (e2) {}
  }
  function onEnded(e) {
    try {
      var v = e.target; if (!v || v.tagName !== 'VIDEO' || skipVideo(v)) { return; }
      var i = vinfo(v);
      if (!i.state.done) { i.state.done = true; send('eng_video', { video_action: 'complete', video_title: i.title, video_url: i.url, item_index: i.idx, section: sectionName(v) }, true); }
    } catch (e3) {}
  }
  D.addEventListener('play', onPlay, true);
  D.addEventListener('timeupdate', onTime, true);
  D.addEventListener('ended', onEnded, true);

  /* ---------- click handling ---------- */
  function mediaIndex(el) {
    var li = closest(el, '[data-media-position]');
    if (li) { return Number(li.getAttribute('data-media-position')) || undefined; }
    var t = clean((closest(el, 'button') || el).textContent, 60), m = /media\s+(\d+)/i.exec(t);
    if (m) { return Number(m[1]); }
    var slide = closest(el, '.product__media-item'), sib = slide && slide.parentNode ? slide.parentNode.children : null, i;
    if (sib) { for (i = 0; i < sib.length; i++) { if (sib[i] === slide) { return i + 1; } } }
    return undefined;
  }
  function labelOf(el) {
    var t = clean(el.getAttribute && (el.getAttribute('aria-label') || el.getAttribute('title')) || '', 80);
    if (t) { return t; }
    t = clean(el.textContent, 80);
    if (t) { return t; }
    var img = el.querySelector ? el.querySelector('img[alt]') : null;
    return img ? clean(img.getAttribute('alt'), 80) : '';
  }
  function cardTitle(card) {
    var h = card && card.querySelector ? card.querySelector('h1,h2,h3,h4,[class*="title"]') : null;
    return h ? clean(h.textContent, 80) : '';
  }
  function inCart(el) { return !!closest(el, 'cart-items, cart-drawer, #CartDrawer, .cart-drawer, #main-cart-items, .cart__items, form[action="/cart"]'); }
  function cartSection(el) { return closest(el, 'cart-drawer, #CartDrawer, .cart-drawer') ? 'drawer' : 'page'; }

  var lastTC = { t: 0, el: null }, lastPU = { t: 0, el: null, used: false };
  function inter(el) { return closest(el, 'button, a, [role="button"], input, summary, label') || el; }
  function rel(a, b) { try { return !!a && !!b && (a === b || a.contains(b) || b.contains(a)); } catch (e) { return false; } }
  function onPointUp(e) {
    try {
      if (e.isTrusted === false) { return; }
      var t = e.target; if (!t || t.nodeType !== 1) { t = t && t.parentElement; } if (!t) { return; }
      lastPU = { t: now(), el: inter(t), used: false };
    } catch (err) {}
  }
  try { D.addEventListener('pointerup', onPointUp, { capture: true, passive: true }); D.addEventListener('touchend', onPointUp, { capture: true, passive: true }); }
  catch (e8) { D.addEventListener('pointerup', onPointUp, true); D.addEventListener('touchend', onPointUp, true); }
  var pendQty = null, lastRemove = 0; /* +/- click is remembered; the theme's own change event right after it is the ONE real quantity change we count */
  function onClick(e) {
    try {
      var t = e.target;
      if (!t || t.nodeType !== 1) { t = t && t.parentElement; }
      if (!t) { return; }
      /* clicks made by scripts (el.click(), e.g. Zipify re-clicking add to cart or checkout) are not user actions on their own.
         Trusted clicks (mouse, keyboard, normal taps) are always counted. A script click is dropped if a trusted click on the same button happened in the last 3 s
         (desktop: Zipify re-click after the real click). It is counted once only when it directly follows a trusted tap (pointerup/touchend) on that button that
         produced no trusted click (phone: Zipify cancels touchend, then clicks by script). With no real input before it, it is ignored. */
      var btn = inter(t), tnow = now();
      if (e.isTrusted !== false) { lastTC = { t: tnow, el: btn }; }
      else if ((lastTC.el && tnow - lastTC.t < 3000 && rel(lastTC.el, btn)) || !(lastPU.el && !lastPU.used && tnow - lastPU.t < 3000 && rel(lastPU.el, btn))) {
        W.__alEngSynth = (W.__alEngSynth || 0) + 1; return;
      } else { lastPU.used = true; W.__alEngTapAccepted = (W.__alEngTapAccepted || 0) + 1; }
      /* existing, already-instrumented zones: homepage rebuild sections (.hpr) and the Find your wallet chooser (.amc3) */
      if (closest(t, '.hpr, [data-track-section], .amc3, [data-amc3-model], [data-amc3-tab], [id*="ashland_model_chooser"], [id*="style-tiles"]')) { return; }
      if (closest(t, 'input[type="text"], input[type="email"], input[type="password"], input[type="tel"], textarea')) { return; }

      var el, a, txt, sec;

      /* ---- cart (page + drawer, any page) ---- */
      el = closest(t, '[name="checkout"], button#checkout, #checkout, a[href="/checkout"], a[href^="/checkout"], .cart__checkout-button, [id*="Checkout-Button"], [id*="CartDrawer-Checkout"]');
      if (el) { if (!throttled('co', 2000)) { send('cart_checkout_click', { section: cartSection(el), link_text: labelOf(el) }); } return; }
      el = closest(t, 'cart-remove-button, .cart-remove-button, a[href*="/cart/change"], .cart-item__remove, [aria-label^="Remove"]');
      if (el && inCart(el)) { lastRemove = now(); if (!throttled('rm', 500)) { send('cart_remove_click', { section: cartSection(el), link_text: clean(el.getAttribute('aria-label') || labelOf(el), 80) }); } return; }
      el = closest(t, '.quantity__button');
      if (el && inCart(el)) { pendQty = { t: now(), name: el.getAttribute('name') || 'button', sec: cartSection(el) }; return; }
      el = closest(t, 'button[type="submit"], button[name*="discount" i], button[class*="discount" i]');
      if (el) {
        var f = closest(el, 'form'), di = f ? f.querySelector('input[name*="discount" i], input[name*="coupon" i], input[id*="discount" i]') : null;
        if (di) { if (!throttled('dc', 800)) { send('cart_discount_apply_click', { section: cartSection(el) }); } return; }
      }

      if (isProduct) {
        /* ---- add to cart (main, upsell, sticky) ---- */
        el = closest(t, '.product-upsell-card__atc-button');
        if (el) {
          var card = closest(el, '.product-upsell-card');
          if (!throttled('uatc', 2000)) { send('pdp_upsell_atc_click', { section: 'pairs_well_with', link_text: cardTitle(card), link_url: card ? pathOf(card.getAttribute('data-product-url')) : '', item_index: indexIn(card, '.product-upsell-card') }, true); }
          return;
        }
        el = closest(t, '.product-form__submit, [id^="ProductSubmitButton"], button[name="add"]');
        if (el) {
          if (throttled('atc', 2000)) { return; }
          var r = el.getBoundingClientRect ? el.getBoundingClientRect() : { top: 0 };
          var sticky = !!closest(el, '[class*="sticky"]:not(.product__column-sticky):not(.product__info-container)');
          send('pdp_atc_click', { section: sticky ? 'sticky' : 'main', location: (r.top >= 0 && r.top < (W.innerHeight || 800)) ? 'above_fold' : 'below_fold', percent_scrolled: scrollPct(), link_text: clean(el.textContent, 40) }, true);
          return;
        }
        el = closest(t, '.shopify-payment-button, shopify-accelerated-checkout, [data-shopify="payment-button"]');
        if (el) { if (!throttled('buy', 2000)) { send('pdp_buy_now_click', { section: 'main', percent_scrolled: scrollPct() }, true); } return; }
        /* ---- gallery ---- */
        el = closest(t, '.thumbnail-list__item, button.thumbnail');
        if (el) { if (!throttled('th', 250)) { send('pdp_gallery_click', { link_text: 'thumbnail', item_index: mediaIndex(el) }, true); } return; }
        el = closest(t, 'media-gallery .slider-button, media-gallery [name="next"], media-gallery [name="previous"]');
        if (el) { if (!throttled('gs', 250)) { send('pdp_gallery_click', { link_text: 'slide_' + (el.getAttribute('name') || 'arrow') }, true); } return; }
        el = closest(t, '.product__media-toggle, modal-opener, .product__modal-opener, [class*="image-magnify"]');
        if (el) { if (!throttled('zm', 400)) { send('pdp_gallery_click', { link_text: 'zoom_open', item_index: mediaIndex(el) }, true); } return; }
        /* ---- options ---- */
        el = closest(t, 'variant-selects label, variant-selects input, variant-selects fieldset');
        if (el) {
          var inp = el.tagName === 'LABEL' ? D.getElementById(el.getAttribute('for') || '') : (el.tagName === 'INPUT' ? el : null);
          if (inp) {
            var k = 'op' + inp.name + inp.value;
            if (!throttled(k, 600)) {
              var gone = inp.disabled || /(^|\s)disabled(\s|$)/.test(inp.className || '') || inp.getAttribute('aria-disabled') === 'true';
              send(gone ? 'pdp_option_sold_out_click' : 'pdp_option_click', { option_name: clean(inp.name, 40), option_value: clean(inp.value, 40), availability: gone ? 'sold_out' : 'available' }, true);
            }
          }
          return;
        }
        el = closest(t, '.custom__bundle__section a, .product__section a');
        if (el) { if (!throttled('col', 400)) { send('pdp_color_click', { link_text: clean((el.querySelector('.title_cmb') || el).textContent, 40), link_url: pathOf(el.href), item_index: indexIn(closest(el, '.product__section'), '.product__section') }, true); } return; }
        el = closest(t, '.quantity__button');
        if (el) { if (!throttled('pq', 300)) { send('pdp_qty_click', { link_text: el.getAttribute('name') || 'button' }, true); } return; }
        /* ---- accordions / tabs ---- */
        el = closest(t, '.acc_title, .content_tabs_section > h3, .accordion summary, details > summary, [data-accordion-trigger]');
        if (el) {
          var ttl = clean(el.textContent, 60), host = closest(el, '.content_tabs_section, details, .accordion');
          if (!throttled('ac' + ttl, 500)) {
            setTimeout(function () {
              try {
                var open = false;
                if (host && host.tagName === 'DETAILS') { open = host.open; }
                else if (host) { var ans = host.querySelector('.accordion-list-answer, .accordion__content'); open = !!(ans && ans.offsetHeight > 0) || /(^|\s)(active|open|is-open|expanded)(\s|$)/.test(host.className || ''); }
                if (open) { send('pdp_accordion_open', { link_text: ttl, section: 'accordion' }, true); }
              } catch (e5) {}
            }, 150);
          }
          return;
        }
        /* ---- trust badges, free shipping, reviews, size chart, care, cross links ---- */
        el = closest(t, '.apbx__item');
        if (el) { if (!throttled('tb', 400)) { send('pdp_link_click', { section: 'trust_badge', link_text: clean((el.querySelector('.apbx__title') || el).textContent, 60) }, true); } return; }
        el = closest(t, '.pdp-free-ship');
        if (el) { if (!throttled('fs', 400)) { send('pdp_link_click', { section: 'free_shipping', link_text: clean(el.textContent, 60) }, true); } return; }
        el = closest(t, '.ruk_rating_snippet, [class*="reviews-widget"], [id*="reviews"] a, [class*="REVIEWSio"], a[href*="reviews.io"]');
        if (el) { if (!throttled('rv', 400)) { send('pdp_link_click', { section: 'reviews', link_text: labelOf(el) || 'stars' }, true); } return; }
        a = closest(t, 'a[href]');
        if (a) {
          var inUp = closest(a, '.product-upsell-card'), inRel = closest(a, 'product-recommendations, .related-products');
          txt = labelOf(a); var hr = a.getAttribute('href') || '';
          var cls = inUp ? 'pairs_well_with' : inRel ? 'related' :
            /size|chart|compare|fit/i.test(txt + ' ' + hr) ? 'size_chart' :
            /care|condition/i.test(txt + ' ' + hr) ? 'care' :
            /return|guarantee|warranty/i.test(txt + ' ' + hr) ? 'guarantee' :
            /shipping/i.test(txt + ' ' + hr) ? 'shipping' :
            /review/i.test(txt + ' ' + hr) ? 'reviews' : '';
          if (cls && closest(a, '#MainContent') && !throttled('pl' + hr, 500)) {
            send('pdp_link_click', { section: cls, link_text: inUp ? (cardTitle(inUp) || txt) : txt, link_url: pathOf(a.href), item_index: inUp ? indexIn(inUp, '.product-upsell-card') : undefined }, true);
            return;
          }
        }
      }

      /* ---- product tiles / product links (collection, search, home legacy, article, page) ---- */
      a = closest(t, 'a[href*="/products/"]');
      if (a && !isProduct && closest(a, '#MainContent')) {
        if (throttled('pc' + a.getAttribute('href'), 500)) { return; }
        var scope = closest(a, '.shopify-section') || D, links = scope.querySelectorAll('a[href*="/products/"]'), seenH = {}, pos = 0, found = 0, j, hp;
        for (j = 0; j < links.length; j++) { hp = (links[j].getAttribute('href') || '').split('?')[0]; if (!seenH[hp]) { seenH[hp] = 1; pos++; if (links[j] === a || hp === (a.getAttribute('href') || '').split('?')[0]) { found = pos; break; } } }
        send('col_product_click', { section: sectionName(a), link_text: cardTitle(closest(a, 'li, .grid__item, .card-wrapper, .card, [class*="product"]')) || labelOf(a), link_url: pathOf(a.href), item_index: found || undefined });
        return;
      }

      /* ---- collection filters (summary / links / buttons; inputs are handled on change) ---- */
      el = closest(t, 'facet-filters-form, .facets, .facet-filters, [id*="FacetFilters"], [class*="facets"], [class*="collection-filter"], [data-filter]');
      if (el && closest(t, 'summary, button, a')) {
        if (!throttled('ff' + labelOf(closest(t, 'summary, button, a')), 500)) { send('col_filter_click', { section: 'filter', link_text: labelOf(closest(t, 'summary, button, a')) }); }
        return;
      }

      /* ---- generic click (CTA / nav / footer / link) ---- */
      el = closest(t, 'a[href], button, [role="button"], summary, input[type="submit"]');
      if (!el) { return; }
      if (el.tagName === 'INPUT') { return; }
      var href = el.tagName === 'A' ? (el.getAttribute('href') || '') : '', lt = labelOf(el);
      var sec2 = sectionName(el);
      var type = /^(mailto|tel):/i.test(href) ? 'contact' :
        (el.tagName === 'A' && el.hostname && el.hostname !== W.location.hostname ? 'outbound' :
        (sec2 === 'footer' ? 'footer' : (sec2 === 'header' || closest(el, 'nav, .header') ? 'nav' :
        (el.tagName === 'BUTTON' || /(^|\s)(button|btn)(\s|$)/.test(el.className || '') || el.getAttribute('role') === 'button' ? 'cta' : 'link'))));
      if (throttled('gc' + type + lt + href, 500)) { return; }
      send('eng_click', { link_type: type, section: sec2, link_text: lt, link_url: pathOf(el.href || href), location: (function () { try { var r2 = el.getBoundingClientRect(); return (r2.top >= 0 && r2.top < (W.innerHeight || 800)) ? 'above_fold' : 'below_fold'; } catch (e6) { return undefined; } })(), percent_scrolled: scrollPct() }, isProduct);
    } catch (err) {}
  }
  function indexIn(el, sel) {
    try {
      if (!el) { return undefined; }
      var list = D.querySelectorAll(sel), i;
      for (i = 0; i < list.length; i++) { if (list[i] === el) { return i + 1; } }
    } catch (e) {}
    return undefined;
  }
  try { D.addEventListener('click', onClick, { capture: true, passive: true }); } catch (e7) { D.addEventListener('click', onClick, true); }

  /* ---------- change events: filters, sort, cart quantity inputs, variant dropdowns ---------- */
  D.addEventListener('change', function (e) {
    try {
      var t = e.target; if (!t || t.nodeType !== 1) { return; }
      if (closest(t, '.hpr, [data-track-section], .amc3')) { return; }
      var el = closest(t, 'select[name="sort_by"], #SortBy, [id*="SortBy"], [name*="sort_by"]');
      if (el) { send('col_filter_click', { section: 'sort', option_value: clean(el.value, 40), link_text: 'sort_by' }); return; }
      if (closest(t, 'facet-filters-form, .facets, .facet-filters, [id*="FacetFilters"], [class*="facets"]') && /^(INPUT|SELECT)$/.test(t.tagName)) {
        if (!throttled('fc' + t.name + t.value, 400)) { send('col_filter_click', { section: 'filter', option_name: clean(t.name, 40), option_value: clean(t.type === 'checkbox' ? (t.checked ? 'on:' : 'off:') + t.value : t.value, 40) }); }
        return;
      }
      if (t.classList && t.classList.contains('quantity__input') && inCart(t)) { var pq = pendQty; pendQty = null;
        if (pq && now() - pq.t < 1500) { if (!throttled('cq', 150)) { send('cart_qty_change', { section: pq.sec, link_text: pq.name }); } }
        else if (now() - lastRemove > 1500 && !throttled('cqi', 400)) { send('cart_qty_change', { section: cartSection(t), link_text: 'input' }); }
        return; }
      if (isProduct && closest(t, 'variant-selects select')) { var so = t.options && t.options[t.selectedIndex], sold = !!(so && (so.disabled || /sold out|unavailable/i.test(so.text))); send(sold ? 'pdp_option_sold_out_click' : 'pdp_option_click', { option_name: clean(t.name, 40), option_value: clean(t.value, 40), availability: sold ? 'sold_out' : 'available' }, true); }
    } catch (err) {}
  }, true);

  /* discount field: only records that the field was focused, never its value */
  D.addEventListener('focusin', function (e) {
    try {
      var t = e.target;
      if (t && t.tagName === 'INPUT' && /discount|coupon|promo/i.test((t.name || '') + (t.id || '')) && once('disc')) { send('cart_discount_focus', { section: cartSection(t) }); }
    } catch (err) {}
  }, true);

  /* ---------- page-type one-offs + start ---------- */
  function start() {
    observeSections();
    if (PT === '404' && once('404')) { send('eng_404', { link_url: pathOf(D.referrer) || 'direct' }); }
    checkScroll();
  }
  if (D.readyState === 'complete') { setTimeout(start, 300); } else { W.addEventListener('load', function () { setTimeout(start, 300); }); }
  W.__alEng = { version: VERSION, pageType: PT, handle: handle };
})();
